/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, ReactNode} from 'react';
import {
  Checkbox as MuiCheckbox,
  CheckboxProps,
  FormControlLabel,
  makeStyles
} from '@material-ui/core';
import clsx from 'clsx';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import {DLSShadowStyle} from '../../../types/types';

const CHECKED_ICON_BACKGROUND_IMAGE =
  'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Cpath fill-rule=\'evenodd\' clip-rule=\'evenodd\' d=\'M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z\' fill=\'%23fff\'/%3E%3C/svg%3E")';

interface CheckboxTypes extends CheckboxProps {
  label?: string | JSX.Element;
  labelProps?: boolean;
  customLabel?: object;
  name?: string;
  labelwidth?: string;
  addRightMargin?: boolean;
  onChange?: (e: ChangeEvent<{name?: string; value: unknown;}>, child?: ReactNode) => void;
};

const useStyles = makeStyles<CheckboxTypes>(() => ({
  root : {
    padding   : 9,
    '&:hover' : {
      backgroundColor : 'transparent',
      '& $icon'       : {
        borderColor : `2px solid ${dlsColor('aaBlue')}`
      },
      '& $checkedIcon' : {
        width     : 20,
        height    : 20,
        margin    : -1,
        boxShadow : dlsShadow(7, DLSShadowStyle.Inset)
      }
    },
    '&:active' : {
      '& $icon' : {
        boxShadow   : dlsShadow(7.2, DLSShadowStyle.Inset),
        borderColor : dlsColor('darkTeal'),
        '&:after'   : {
          display : 'none !important'
        }
      },
      '& $checkedIcon' : {
        boxShadow       : 'none',
        borderColor     : dlsColor('darkTeal'),
        backgroundColor : dlsColor('darkTeal'),
        '&:before'      : {
          transform : 'translate(-50%, -50%) scale(1)'
        }
      }
    },
    '&$disabled' : {
      '& $icon' : {
        borderColor     : dlsColor('grey4'),
        backgroundColor : dlsColor('grey4')
      },
      '& $checkedIcon' : {
        '&:before' : {
          opacity : 0.5
        }
      }
    }
  },
  checked : {},
  icon    : {
    width          : 18,
    height         : 18,
    border         : `2px solid ${dlsColor('grey2')}`,
    display        : 'inline-block',
    position       : 'relative',
    boxSizing      : 'border-box',
    alignItems     : 'center',
    transition     : 'all 200ms ease-in-out',
    borderRadius   : 2,
    justifyContent : 'center',
    '&:after'      : {
      top          : '50%',
      left         : '50%',
      width        : 26,
      height       : 26,
      border       : `2px solid ${dlsColor('skyBlue')}`,
      content      : '""',
      opacity      : 0,
      position     : 'absolute',
      boxSizing    : 'border-box',
      transform    : 'translate(-50%, -50%)',
      transition   : 'opacity 200ms',
      borderRadius : 4
    }
  },
  checkedIcon : {
    borderColor     : dlsColor('aaBlue'),
    backgroundColor : dlsColor('aaBlue'),
    '&:before'      : {
      backgroundImage  : CHECKED_ICON_BACKGROUND_IMAGE,
      top              : '50%',
      left             : '50%',
      width            : 18,
      height           : 18,
      content          : '""',
      display          : 'block',
      position         : 'absolute',
      transform        : 'translate(-50%, -50%)',
      backgroundSize   : 'contain',
      backgroundRepeat : 'no-repeat'
    }
  },
  indeterminateIcon : {
    borderColor     : dlsColor('aaBlue'),
    backgroundColor : dlsColor('aaBlue'),
    '&:before'      : {
      backgroundColor : dlsColor('white'),
      backgroundImage : 'none',
      borderRadius    : 1,
      content         : '""',
      display         : 'block',
      height          : 2,
      left            : '50%',
      position        : 'absolute',
      top             : '50%',
      transform       : 'translate(-50%, -50%)',
      width           : 10
    }
  },
  label : (props: CheckboxTypes) => (props.customLabel as any) ||{
    color         : dlsColor('grey1'),
    width         : props.labelwidth || 'auto',
    fontSize      : '14px',
    overflow      : 'hidden',
    fontFamily    : 'Inter',
    lineHeight    : '20px',
    whiteSpace    : 'nowrap',
    textOverflow  : 'ellipsis',
    letterSpacing : '0'
  },
  removeMargin : (props: CheckboxTypes) => (!props.addRightMargin ? {marginRight: '0px'} : {})
}));

/**
 * A customized Material UI Checkbox designed for the DLS system. 
 * Replaces standard SVG icons with custom CSS-styled spans to handle 
 * complex focus rings and state transitions.
 */
export const CheckBox = ({
  label,
  labelProps,
  name,
  onChange,
  addRightMargin = true,
  ...props
}: CheckboxTypes) => {
  const classes                = useStyles({...props, addRightMargin});
  const {customLabel, ...rest} = props;

  function handleChange(e: ChangeEvent<{name?: string; value: unknown;}>, child: ReactNode) {
    if (name) {
      e.target.name = name;
    }
    onChange?.(e, child);
  }

  return (
    <FormControlLabel classes={{
      label : labelProps ? classes.label : '',
      root  : classes.removeMargin
    }}
    control={
      <MuiCheckbox disableRipple disableFocusRipple
        className={classes.root}
        classes={{
          root    : classes.root,
          checked : classes.checked
        }}
        checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
        indeterminateIcon={
          <span className={clsx(classes.icon, classes.checkedIcon, classes.indeterminateIcon)} />
        }
        icon={<span className={classes.icon} />}
        color='primary'
        onChange={handleChange}
        {...rest} />
    } label={label} />
  );
};

export default React.memo(CheckBox);
